// External dependencies
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { FormProvider, useController, useForm, useWatch } from 'react-hook-form@latest';
import { ErrorMessage } from '@hookform/error-message';
import { RiInformationLine } from 'react-icons/ri';

// Utilities
import { _isEmptyString, _isNotBlank, _isNotEmpty, _notNil } from '@/littledash';
import { formatNumber } from '@/helpers';

// Model types
import type { ID } from '@/model/Common.model';
import type { MetadataField } from '@/model/Metadata.model';
import type { StudyApiId } from '@/model/Study.model';
import type {
  ClassificationType,
  Treatment,
  TreatmentField,
  TreatmentMethod,
  TreatmentVolumeUnit,
} from '@/model/Treatment.model';

// Support modules
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { useFetchEntity } from '@/support/Hooks/fetch';
import { ModalActions, ModalContainer, ModalHeader } from '@/utils/modal';

// Internal components
import Loading from '@/components/Loading';
import Banner from '@/components/UI/Banner';
import Radio from '@/components/UI/FormElements/Radio';
import FormRender from '@/components/UI/FormRender';
import InvalidMetadataBanner from '@/components/UI/InvalidMetadataBanner/InvalidMetadataBanner';
import Select from '@/components/UI/Select';
import type { SelectOption } from '@/components/UI/Select/Select';
import { classificationTypes, treatmentMetadataFields } from '@/components/Studies/Treatments/Treatment.utils';

// Relative imports
import {
  dosingMethodOptions,
  dosingMethods,
  featureSelector,
  generateTreatmentLabel,
  getDefaults,
  handleTreatmentSubmit,
  isValidMetadataValue,
  validateTreatmentMetadata,
} from './AddTreatmentForm.utils';
import DosingMethodField from './DosingMethodField';
import type { TreatmentForm } from './TreatmentForm.model';
import { TreatmentLookup } from './TreatmentLookup';

export { handleTreatmentSubmit } from './AddTreatmentForm.utils';

export interface AddTreatmentFormProps {
  treatment: Treatment;
  study?: { api_id: StudyApiId };
  metadata: Array<MetadataField>;
  onSubmit: (treatment: Treatment, edit: boolean, treatmentId?: ID) => void;
  onCancel: () => void;
  classificationType: ClassificationType;
}

const AddTreatmentForm: FC<AddTreatmentFormProps> = ({
  treatment,
  study,
  metadata,
  onSubmit,
  onCancel,
  classificationType,
}) => {
  const { diseaseInductionEnabled, treatmentLabelEditEnabled } = useSelector(featureSelector);
  const [hasMovedAnimals, setHasMovedAnimals] = useState<boolean>(false);

  const { invalidFields: initialInvalidFields, hasInvalidFields: initialHasInvalidFields } = useMemo(
    () => validateTreatmentMetadata(treatment, metadata),
    [treatment, metadata]
  );

  // Track which invalid fields have been corrected by the user
  const [correctedFields, setCorrectedFields] = useState<Set<string>>(new Set());

  // Compute metadata validation state
  const { metadataBySlug, invalidFields, hasInvalidFields, cleanedTreatment } = useMemo(() => {
    // Create a map of metadata fields by slug for validation
    const metadataBySlug = metadata.reduce<Record<string, MetadataField>>((acc, meta) => {
      acc[meta.slug] = meta;
      return acc;
    }, {});

    // Filter out corrected fields from the invalid fields
    const invalidFields = Object.entries(initialInvalidFields).reduce<Record<string, { title: string; slug: string }>>(
      (acc, [slug, value]) => {
        if (!correctedFields.has(slug)) {
          acc[slug] = value;
        }
        return acc;
      },
      {}
    );

    const hasInvalidFields = _isNotEmpty(Object.keys(invalidFields));

    // Clean treatment by removing invalid metadata
    const cleanedTreatment =
      initialHasInvalidFields && _notNil(treatment)
        ? {
            ...treatment,
            metadata: treatment.metadata?.filter((meta) => !Object.keys(initialInvalidFields).includes(meta.slug)),
          }
        : treatment;

    return { metadataBySlug, invalidFields, hasInvalidFields, cleanedTreatment };
  }, [metadata, initialInvalidFields, correctedFields, treatment, initialHasInvalidFields]);

  const defaultTreatmentMethod: TreatmentMethod = classificationType === 'disease_induction' ? 'custom' : 'volume';

  const [selectedTreatmentMethod, setSelectedTreatmentMethod] = useState<TreatmentMethod>(
    treatment?.type ?? defaultTreatmentMethod
  );

  const [selectedTreatmentClassification, setSelectedTreatmentClassification] = useState<ClassificationType>(
    treatment?.treatment_classification ?? classificationType
  );

  const [selectedVolumeUnit, setSelectedVolumeUnit] = useState<TreatmentVolumeUnit>(
    treatment?.treatment_volume_unit ?? (treatment?.calculates?.unit?.display_unit as TreatmentVolumeUnit) ?? 'µL'
  );

  const metaFieldValues = treatmentMetadataFields(metadata, cleanedTreatment);
  const treatmentClassification = classificationTypes[classificationType];

  const {
    entity: treatmentForm,
    entityLoading: treatmentFormLoading,
    entityUpdating: treatmentFormUpdating,
    fetchEntity: fetchTreatmentForm,
  } = useFetchEntity<TreatmentForm>({
    entityType: 'treatmentForm',
    params: { treatmentMethod: selectedTreatmentMethod },
  });

  const { invoke: checkHasMovedAnimals } = useApiHook({
    endpoint: 'GET /api/v1/studies/{studyApiId}/treatments/{treatmentApiId}/has-moved-animals',
    invokeOnInit: false,
    options: { onError: { toast: false, capture: false, throw: false } },
  });

  const edit = _isNotEmpty(treatment);

  // Form uses dynamic dosing method keys (e.g. volume.dose.default_value) that aren't
  // statically typed on TreatmentFromForm. Omitting the generic preserves v6 behavior.
  const formMethods = useForm({
    mode: 'onChange',
  });

  const {
    handleSubmit,
    control,
    reset,
    getValues,
    watch,
    formState: { isSubmitting, errors, touchedFields, isSubmitted },
  } = formMethods;
  const {
    field: { onBlur: treatmentOnBlur, onChange: treatmentOnChange, value: treatmentValue },
  } = useController({
    control,
    defaultValue: treatment?.treatment_type ?? null,
    name: 'treatment_type',
    rules: { required: `${treatmentClassification.key} is required` },
  });

  const {
    field: { onBlur: dosingMethodOnBlur, onChange: dosingMethodOnChange, value: dosingMethodValue },
  } = useController({
    control,
    name: 'type',
    defaultValue: treatment?.type ?? dosingMethodOptions[0].value,
    rules: { required: true },
  });

  const {
    field: { onChange: treatmentClassificationOnChange },
  } = useController({
    control,
    name: 'treatment_classification',
    defaultValue: treatment?.treatment_classification ?? 'treatment',
    rules: { required: 'Treatment Classification is required' },
  });

  const {
    field: { onBlur: treatmentVolumeUnitOnBlur, onChange: treatmentVolumeUnitOnChange },
  } = useController({
    control,
    name: 'treatment_volume_unit',
    rules: { required: 'Treatment volume unit is required' },
  });

  const {
    field: { onChange: treatmentLabelOnChange, onBlur: treatmentLabelOnBlur, value: treatmentLabelValue },
  } = useController({
    control,
    name: 'treatment_label',
    defaultValue: treatment?.treatment_label ?? '',
    rules: {
      required: 'Treatment Label is required',
      maxLength: { value: 255, message: 'Treatment Label must be 255 characters or less' },
    },
  });

  const classificationTypeOptions = Object.entries(classificationTypes)
    .filter(([key]) => key !== 'disease_induction')
    .map(([key, value]) => ({
      value: key as ClassificationType,
      label: value.title,
      sublabel: value.sublabel,
    }));

  // Watch the invalid field values to trigger re-evaluation when they change
  const { invalidFieldSlugs, watchFields } = useMemo(() => {
    const slugs = Object.keys(initialInvalidFields);
    return {
      invalidFieldSlugs: slugs,
      watchFields: slugs.map((slug) => `meta.${slug}`),
    };
  }, [initialInvalidFields]);
  const watchedInvalidFields = useWatch({ name: watchFields, control });

  // Check if invalid fields have been corrected when form values change
  useEffect(() => {
    const allValues = getValues();
    const watchedMeta = allValues?.meta;

    if (!initialHasInvalidFields || !_notNil(watchedMeta)) {
      return;
    }

    // Use functional update to avoid stale closure issues and remove correctedFields from deps
    setCorrectedFields((prevCorrectedFields) => {
      const newCorrectedFields = new Set(prevCorrectedFields);

      invalidFieldSlugs.forEach((slug) => {
        const currentValue = watchedMeta[slug];
        const metadataField = metadataBySlug[slug];

        // Only consider a field "corrected" if user has selected an actual valid value
        // Empty/null values should not be considered corrected
        const hasValue = _isNotBlank(currentValue);

        if (_notNil(metadataField) && hasValue) {
          const isValid = isValidMetadataValue(currentValue, metadataField);

          if (isValid) {
            newCorrectedFields.add(slug);
          } else {
            newCorrectedFields.delete(slug);
          }
        } else {
          // If value is empty/null, ensure it's not marked as corrected
          newCorrectedFields.delete(slug);
        }
      });

      // Only update if the set has changed (React will skip the update if we return the same reference)
      if (
        newCorrectedFields.size !== prevCorrectedFields.size ||
        ![...newCorrectedFields].every((f) => prevCorrectedFields.has(f))
      ) {
        return newCorrectedFields;
      }
      return prevCorrectedFields;
    });
  }, [watchedInvalidFields, initialHasInvalidFields, metadataBySlug, getValues, invalidFieldSlugs]);

  useEffect(() => {
    if (_isNotEmpty(selectedTreatmentMethod)) {
      dosingMethodOnChange(selectedTreatmentMethod);
      fetchTreatmentForm();
    }
  }, [selectedTreatmentMethod]);

  useEffect(() => {
    if (_isNotEmpty(selectedTreatmentClassification)) {
      treatmentClassificationOnChange(selectedTreatmentClassification);
    }
  }, [selectedTreatmentClassification]);

  useEffect(() => {
    if (_isNotEmpty(selectedVolumeUnit)) {
      treatmentVolumeUnitOnChange(selectedVolumeUnit);
    }
  }, [selectedVolumeUnit]);

  useEffect(() => {
    if (edit && _notNil(treatment?.api_id) && _notNil(study?.api_id)) {
      checkHasMovedAnimals({
        path: {
          studyApiId: study!.api_id,
          treatmentApiId: treatment.api_id,
        },
      }).then((result) => {
        if (result?.type === 'success') {
          setHasMovedAnimals(result.body?.data ?? false);
        }
      });
    }
  }, [edit, treatment?.api_id, study?.api_id]);

  // Track previous values to detect actual user changes
  const prevValuesRef = useRef<{
    treatmentName?: string;
    dosingMethod?: TreatmentMethod;
    doseValue?: string;
    doseUnit?: string;
    volumeValue?: string;
    volumeUnit?: string;
    doseVolumeValue?: string;
    doseVolumeUnit?: string;
  }>({});

  // In edit mode, skip regeneration until form has been fully initialized
  const [formReady, setFormReady] = useState(!edit);

  useEffect(() => {
    if (!treatmentFormLoading && _notNil(treatmentForm)) {
      const formattedFields =
        cleanedTreatment?.fields?.map((field) => {
          if (_notNil(field?.default_display_value)) {
            field.default_display_value = formatNumber(field.default_display_value);
          } else if (_notNil(field?.default_value)) {
            field.default_value = _isEmptyString(field.default_value ?? '') ? '' : formatNumber(field.default_value);
          }
          return field;
        }) ?? [];

      const formattedTreatments = {
        ...cleanedTreatment,
        fields: formattedFields,
      };

      const allFormValues = getValues();
      const currentFormValues: Record<string, unknown> = {
        treatment_type: allFormValues.treatment_type,
        type: allFormValues.type,
        meta: allFormValues.meta,
        treatment_classification: allFormValues.treatment_classification,
        treatment_volume_unit: allFormValues.treatment_volume_unit,
        treatment_label: allFormValues.treatment_label,
      };
      const resetValues: Record<string, unknown> = {
        ...currentFormValues,
        ...getDefaults(treatmentForm.name, treatmentForm.fields, selectedTreatmentMethod, formattedTreatments),
      };

      if (hasInvalidFields) {
        if (!_notNil(resetValues.meta)) {
          resetValues.meta = {};
        }
        const metaValues = resetValues.meta as Record<string, string | null>;
        Object.keys(invalidFields).forEach((fieldSlug) => {
          metaValues[fieldSlug] = null;
        });
      }

      reset(resetValues);

      // In edit mode, mark form as ready after reset completes so label regeneration can begin
      if (edit && !formReady) {
        // Initialize prevValuesRef with the reset values so the first comparison doesn't trigger regeneration
        const dosingMethodFields = resetValues[treatmentForm.name] as
          | Record<string, { default_value?: string; unit?: { name?: string } }>
          | undefined;
        prevValuesRef.current = {
          treatmentName: (resetValues.treatment_type as { name?: string } | undefined)?.name,
          dosingMethod: selectedTreatmentMethod,
          doseValue: dosingMethodFields?.dose?.default_value?.toString(),
          doseUnit: dosingMethodFields?.dose?.unit?.name,
          volumeValue: dosingMethodFields?.volume?.default_value?.toString(),
          volumeUnit: dosingMethodFields?.volume?.unit?.name,
          doseVolumeValue: dosingMethodFields?.dose_volume?.default_value?.toString(),
          doseVolumeUnit: dosingMethodFields?.dose_volume?.unit?.name,
        };
        setFormReady(true);
      }
    }
  }, [treatmentForm, cleanedTreatment, hasInvalidFields, invalidFields, edit, formReady, selectedTreatmentMethod]);

  // Watch form fields for label auto-generation
  const doseDefaultValue = watch(`${selectedTreatmentMethod}.dose.default_value`);
  const doseUnit = watch(`${selectedTreatmentMethod}.dose.unit`);
  const volumeDefaultValue = watch(`${selectedTreatmentMethod}.volume.default_value`);
  const volumeUnit = watch(`${selectedTreatmentMethod}.volume.unit`);
  const doseVolumeDefaultValue = watch(`${selectedTreatmentMethod}.dose_volume.default_value`);
  const doseVolumeUnit = watch(`${selectedTreatmentMethod}.dose_volume.unit`);

  // Auto-generate treatment label when relevant fields change
  useEffect(() => {
    if (!_notNil(treatmentForm) || !formReady) {
      return;
    }

    const currentValues = {
      treatmentName: treatmentValue?.name,
      dosingMethod: selectedTreatmentMethod,
      doseValue: doseDefaultValue?.toString(),
      doseUnit: doseUnit?.name,
      volumeValue: volumeDefaultValue?.toString(),
      volumeUnit: volumeUnit?.name,
      doseVolumeValue: doseVolumeDefaultValue?.toString(),
      doseVolumeUnit: doseVolumeUnit?.name,
    };

    const prev = prevValuesRef.current;
    const hasChanged =
      prev.treatmentName !== currentValues.treatmentName ||
      prev.dosingMethod !== currentValues.dosingMethod ||
      prev.doseValue !== currentValues.doseValue ||
      prev.doseUnit !== currentValues.doseUnit ||
      prev.volumeValue !== currentValues.volumeValue ||
      prev.volumeUnit !== currentValues.volumeUnit ||
      prev.doseVolumeValue !== currentValues.doseVolumeValue ||
      prev.doseVolumeUnit !== currentValues.doseVolumeUnit;

    if (hasChanged) {
      const label = generateTreatmentLabel(currentValues.treatmentName, selectedTreatmentMethod, {
        doseValue: currentValues.doseValue,
        doseUnit: currentValues.doseUnit,
        volumeValue: currentValues.volumeValue,
        volumeUnit: currentValues.volumeUnit,
        doseVolumeValue: currentValues.doseVolumeValue,
        doseVolumeUnit: currentValues.doseVolumeUnit,
      });

      treatmentLabelOnChange(label);
    }

    prevValuesRef.current = currentValues;
  }, [
    treatmentValue,
    selectedTreatmentMethod,
    treatmentForm,
    formReady,
    doseDefaultValue,
    doseUnit,
    volumeDefaultValue,
    volumeUnit,
    doseVolumeDefaultValue,
    doseVolumeUnit,
  ]);

  const modalTitle = edit ? `Edit ${treatmentClassification.key}` : `Add ${treatmentClassification.key}`;

  const onFormSubmit = useMemo(
    () =>
      handleSubmit((formData) =>
        handleTreatmentSubmit(
          formData as Treatment & Record<string, TreatmentField>,
          metaFieldValues,
          treatment,
          edit,
          onSubmit
        )
      ),
    [handleSubmit, metaFieldValues, treatment, edit, onSubmit]
  );

  return !treatmentFormLoading && _notNil(treatmentForm) ? (
    <ModalContainer size="small" testKey="add-treatment-form-modal">
      <FormProvider {...formMethods}>
        <form onSubmit={onFormSubmit} noValidate data-test-component="AddTreatmentForm" data-test-element="form">
          <ModalHeader title={modalTitle} closeModal={onCancel} className="pa3 bb b--moon-gray bg-white" />
          <div className="overflow-y-auto" style={{ maxHeight: '60vh' }}>
            <div className="ph3 pv3">
              {edit && hasMovedAnimals && (
                <Banner info dismiss={false} className="mb3">
                  Editing is disabled because this treatment was administered to an animal that is no longer in this
                  study.
                </Banner>
              )}
              {hasInvalidFields && (
                <InvalidMetadataBanner
                  invalidFields={Object.fromEntries(
                    Object.entries(invalidFields).map(([key, value]) => [key, value.title])
                  )}
                />
              )}
              {diseaseInductionEnabled && classificationType !== 'disease_induction' && (
                <div className="mb3" data-test-element="treatment-classification-container">
                  {classificationTypeOptions.map((o) => (
                    <Radio
                      value={o.value}
                      id={o.value}
                      label={o.label}
                      key={o.label}
                      sublabel={o.sublabel}
                      disabled={treatment?.in_use === true || hasMovedAnimals}
                      checked={o.value === selectedTreatmentClassification}
                      onChange={(e) => setSelectedTreatmentClassification(e.target.value as ClassificationType)}
                      dataTestId={`${o.label}_radio_test`}
                    />
                  ))}
                </div>
              )}
              <div className="mb3" data-test-element="treatment-lookup-container">
                <label>Name (required)</label>
                <TreatmentLookup
                  value={treatmentValue}
                  onChange={treatmentOnChange}
                  onBlur={treatmentOnBlur}
                  classificationType={classificationType}
                  isDisabled={hasMovedAnimals}
                />
                <ErrorMessage
                  name="treatment_type"
                  errors={errors}
                  render={({ message }) => <small className="red mb3">{message}</small>}
                />
              </div>
              <div className="mb3" data-test-element="dosing-method-container">
                <label>Dosing Method</label>
                <Select
                  defaultValue={dosingMethods?.[selectedTreatmentMethod]}
                  disabled={treatment?.in_use === true || hasMovedAnimals}
                  options={dosingMethodOptions}
                  onChange={(newValue) => setSelectedTreatmentMethod((newValue as SelectOption<TreatmentMethod>).value)}
                  onBlur={dosingMethodOnBlur}
                  className="w-100"
                  isMulti={false}
                />
              </div>
              <div
                className="justify-between flex"
                style={{ gap: 10 }}
                data-test-element="dosing-method-fields-container"
              >
                {!treatmentFormUpdating && _notNil(treatmentForm.fields) ? (
                  (treatmentForm?.fields ?? []).map((field) => (
                    <DosingMethodField
                      key={`${treatmentForm?.name}.${field.name}`}
                      id={`${treatmentForm?.name}.${field.name}`}
                      dosingType={dosingMethodValue}
                      control={control}
                      field={field}
                      isSubmitting={isSubmitting === true || hasMovedAnimals}
                      unitsDisabled={treatment?.in_use === true || hasMovedAnimals}
                      errors={errors}
                      units={field.units.filter((unit) => unit.unit_type === field.name)}
                      allowZero={
                        field.name === 'dose' && (dosingMethodValue === 'custom' || dosingMethodValue === 'dose_volume')
                      }
                    />
                  ))
                ) : (
                  <div className="flex align-center w-100 pt5-ns">
                    <Loading txt="" loader />
                  </div>
                )}
              </div>
              {!treatmentFormUpdating && _notNil(treatmentForm.calculates) && (
                <div className="mb3" data-test-element="dosing-volume-unit">
                  <label>{treatmentForm.calculates.label}</label>
                  <Select
                    defaultValue={treatmentForm.calculates.options?.find((opt) => opt.value === selectedVolumeUnit)}
                    options={treatmentForm.calculates.options ?? []}
                    onChange={(newValue) => {
                      if (_notNil(newValue)) {
                        setSelectedVolumeUnit((newValue as SelectOption<TreatmentVolumeUnit>).value);
                      }
                    }}
                    onBlur={treatmentVolumeUnitOnBlur}
                    isMulti={false}
                  />
                </div>
              )}
              <div className="bt bb mb2 b--moon-gray pt3" data-test-element="treatment-label-container">
                <label>Treatment Label (required)</label>
                {treatmentLabelEditEnabled && (
                  <span className="bg-washed-blue near-black flex f6 pv2 pl2 mb3 items-center">
                    <RiInformationLine />
                    <span className="pl2">Auto-generated. Customise to differentiate treatments.</span>
                  </span>
                )}
                <input
                  type="text"
                  value={treatmentLabelValue ?? ''}
                  onChange={(e) => treatmentLabelOnChange(e.target.value)}
                  onBlur={treatmentLabelOnBlur}
                  disabled={hasMovedAnimals || !treatmentLabelEditEnabled}
                  maxLength={255}
                  required={true}
                  data-testid="treatment-label-input"
                />
                <div className="mb2">
                  {(touchedFields?.treatment_label === true || isSubmitted) && (
                    <ErrorMessage
                      name="treatment_label"
                      errors={errors}
                      render={({ message }) => <small className="red">{message}</small>}
                    />
                  )}
                </div>
              </div>
              <div className="pb3" data-test-element="metadata-fields-container">
                <FormRender
                  fields={metaFieldValues}
                  disabled={hasMovedAnimals}
                  invalidFields={new Set(Object.keys(invalidFields))}
                  hookFormVersion="latest"
                />
              </div>
            </div>
          </div>
          <ModalActions
            className="pa3 bt b--moon-gray"
            onSubmit={onFormSubmit}
            onCancel={onCancel}
            submitBtnText={edit ? `Update` : `Add`}
            cancelBtnText="Cancel"
            submitButtonProps={{
              disabled: hasMovedAnimals || hasInvalidFields,
              loading: isSubmitting,
            }}
          />
        </form>
      </FormProvider>
    </ModalContainer>
  ) : (
    <></>
  );
};

export default AddTreatmentForm;
